'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSession } from './lib/auth/client';
import { ApiError } from './lib/errors/api-error';
import { useCartStore } from './stores/useCartStore';

function CartSessionManager() {
  const { data: session } = useSession();
  const resetCart = useCartStore((state) => state.resetCart);
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = session?.user?.id || null;

    // If user ID changed (different user logged in) or user logged out, reset cart
    if (previousUserIdRef.current !== null && previousUserIdRef.current !== currentUserId) {
      resetCart();
    }

    // Update ref with current user ID
    previousUserIdRef.current = currentUserId;
  }, [session?.user?.id, resetCart]);

  return null;
}

// Codes that should escape React Query and bubble up to error.tsx / global-error.tsx.
export const FATAL_BOUNDARY_CODES = new Set(['STORE_ACCESS_DENIED', 'UNAUTHORIZED']);

export function shouldThrowToBoundary(error: unknown): boolean {
  return error instanceof ApiError && FATAL_BOUNDARY_CODES.has(error.code);
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            retry: 1,
            // Without this, React Query swallows rejections into query.error
            // and error.tsx never renders for fatal codes.
            throwOnError: shouldThrowToBoundary,
          },
          mutations: {
            throwOnError: shouldThrowToBoundary,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <CartSessionManager />
      {children}
    </QueryClientProvider>
  );
}
